import { STATIC_PATH_FALLBACK } from 'constants/environment';
import { FunnelId } from 'constants/funnel';
import clsx from 'clsx';
import { Author, BlogDate, StructuredData, Tags } from 'components/Pages/Blog';
import NotFound from 'components/Pages/NotFound';
import ConversionBar from 'components/Shared/ConversionBar';
import GetInTouch from 'components/Shared/GetInTouch';
import RecentStories from 'components/Shared/RecentStories';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import BlockLoaderNonFields from 'components/ui/ContentBlocks/BlockLoaderNonFields';
import Image from 'components/ui/Image';
import Layout from 'components/ui/Layout';
import config from 'config';
import GET_BLOG_PAGE from 'data/graphql/queries/blogPage';
import GET_BLOG_STORIES from 'data/graphql/queries/blogStories';
import GET_FUNNELS from 'data/graphql/queries/funnels';
import { getMainFooter, skipServerSideBuild } from 'data/page/utils';
import { getBrand } from 'data/page/utils/getBrand';
import { getAuthorsBaseSlug } from 'data/utils/author';
import { useTranslation } from 'hooks/useTranslation';
import {
  contentfulClientPreview,
  fetchContentfulGql,
  getAlternatePages,
} from 'services/contentful';
import { getContentfulLocale } from 'utils/locale';
import { useLivePreview } from 'hooks/useLivePreview';
import TableOfContents, { getTitles } from 'components/Shared/TableOfContents';
import styles from 'components/Pages/Blog/Article.module.scss';
import Inserts from 'components/Shared/Inserts/Inserts';
import type { GetStaticPaths, GetStaticProps } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import type { AlternateSlug } from 'types/AlternateSlugs';
import type { BlogItem } from 'types/Blog';
import type { Brand } from 'types/Brand';
import type { IBlogPageFields } from 'types/contentful/generated';
import type { FooterItemType } from 'types/Footer';
import type { ConfigLocale, PageData, RecentStoriesItem } from 'types/PageData';
import '@contentful/live-preview/style.css';

type ArticleProps = {
  entry: BlogItem;
  preview: boolean;
  sidebar: boolean;
  alternateSlugs: AlternateSlug[];
  footer: FooterItemType | null;
  authorBaseSlug?: string | null;
  brand: Brand | null;
  recentStoriesItems?: RecentStoriesItem[];
};

const Article = ({
  entry,
  preview,
  sidebar,
  alternateSlugs,
  footer,
  authorBaseSlug,
  brand,
  recentStoriesItems,
}: ArticleProps): JSX.Element => {
  const { t } = useTranslation();
  const { updatedEntity, inspectorProps } = useLivePreview(entry);
  const titles = getTitles(entry);
  const funnelId = updatedEntity?.funnel
    ? updatedEntity.funnel.sys.id
    : FunnelId.CONTACT_FORM;

  if (!updatedEntity) {
    return <NotFound brand={brand} footer={footer} />;
  }

  const breadcrumbs = [
    {
      link: updatedEntity.slug,
      title: updatedEntity.title,
    },
  ];

  const { bannerImage, isTableOfContentsEnabled, cta } = updatedEntity;

  const isTableOfContentsShown = titles && isTableOfContentsEnabled;

  return (
    <Layout
      preview={preview}
      searchIndex={!entry.disableIndexing && !entry.disableLocale}
      title={entry.metaTitle || ''}
      metaDescription={entry.metaDescription}
      alternateSlugs={alternateSlugs}
      alternateSlugsPrefix="blog"
      ogImage={bannerImage ? `https:${bannerImage.url}?w=400` : ''}
      footer={footer}
      noMetaTitle={entry.title || ''}
      brand={brand}
      overflowXHidden={false}
    >
      <StructuredData entry={updatedEntity} />
      <div className={styles.article}>
        <div className={styles.headContainer}>
          <Breadcrumbs
            containerClassNames={styles.breadcrumbsContainerMobile}
            prefix="blog"
            crumbs={breadcrumbs}
          />
          <div className={styles.headContent}>
            <div
              className={clsx(
                styles.headContentInner,
                (isTableOfContentsShown || cta || sidebar) &&
                  styles.headContentInnerStart,
              )}
            >
              <Breadcrumbs
                containerClassNames={styles.breadcrumbsContainerDesktop}
                className={styles.breadcrumbsDesktop}
                prefix="blog"
                crumbs={breadcrumbs}
              />
              <h1 className={clsx(styles.title, 'hyphenate')}>
                <div {...inspectorProps({ fieldId: 'title' })}>
                  {updatedEntity?.title}
                </div>
              </h1>
              <div className={styles.metaDataContainer}>
                <div className={styles.metaData}>
                  <Author
                    {...entry.author}
                    imageClasses={styles.author}
                    authorBaseSlug={authorBaseSlug}
                    oneLineAuthor
                  />
                  <BlogDate
                    createdAt={entry.publishDate || entry.sys.firstPublishedAt}
                    className={styles.date}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {bannerImage && (
          <div className={styles.heroContainer}>
            <div className={styles.hero}>
              <div
                className={clsx(
                  styles.heroInner,
                  !sidebar && styles.heroInnerNoSidebar,
                  (isTableOfContentsShown || cta) &&
                    styles.heroInnerTableOfContents,
                )}
                {...inspectorProps({ fieldId: 'bannerImage' })}
              >
                <div
                  className={styles.heroImage}
                  {...inspectorProps({ fieldId: 'bannerImage' })}
                >
                  <Image
                    src={bannerImage?.url}
                    alt={bannerImage?.description || bannerImage?.title || ''}
                    width={bannerImage?.width}
                    height={bannerImage?.height}
                    contentful
                    priority
                  />
                </div>
              </div>
              {isTableOfContentsShown && (
                <TableOfContents
                  titles={titles}
                  wrapperClasses={styles.tableOfContents}
                />
              )}
            </div>
          </div>
        )}

        <div className={styles.secondary}>
          <div className={styles.secondaryContainer}>
            <div
              className={clsx(
                styles.secondaryContainerInner,
                (isTableOfContentsShown || cta || sidebar) &&
                  styles.secondaryContainerInnerStart,
              )}
            >
              <div>
                {updatedEntity?.content && (
                  <BlockLoaderNonFields entry={updatedEntity} field="content" />
                )}
              </div>
              {entry.tags && (
                <div
                  className={clsx(
                    !recentStoriesItems?.length && styles.tagsContainer,
                  )}
                >
                  <Tags
                    tags={entry.tags.items}
                    headline={t('blog.relatedTags')}
                  />
                </div>
              )}
            </div>
            {entry.cta && (
              <div className={clsx(styles.insertsDesktop)}>
                <Inserts cta={cta} className={styles.stickyContainer} />
              </div>
            )}
            {sidebar && <div className={styles.sidebar}>{sidebar}</div>}
          </div>
          {!!recentStoriesItems?.length && (
            <RecentStories
              entries={recentStoriesItems}
              headline={t('locationPage.recentStories')}
            />
          )}
        </div>
      </div>
      <GetInTouch />
      <ConversionBar funnelId={funnelId} />
    </Layout>
  );
};

interface ArticleParams extends ParsedUrlQuery {
  locale: string;
  article: string;
}

export const getStaticProps: GetStaticProps<
  Omit<PageData, 'contentType'>,
  ArticleParams
> = async ({ params, preview = false }) => {
  const { locale, article } = params || {};
  const contentfulLocale = getContentfulLocale(locale);
  const brand = await getBrand(process.env.BRAND_ID || null, locale);

  const gqEntries = await fetchContentfulGql({
    query: GET_BLOG_PAGE,
    variables: {
      locale: contentfulLocale,
      preview: !!preview,
      slug: article,
    },
    preview,
  });

  const entries = gqEntries.data.blogPageCollection.items;
  const entry =
    entries.filter((item: BlogItem) => !item.disableLocale)[0] || null;

  if (!entry) {
    return {
      notFound: true,
    };
  }

  const funnelIds: string[] = [];
  // eslint-disable-next-line
  // @ts-ignore
  entry.content.items.map((item) => {
    if (item?.__typename === 'BlockFunnel') {
      funnelIds.push(item.sys.id);
    }
  });

  // eslint-disable-next-line
  // @ts-ignore
  let gqFunnels;
  if (funnelIds.length >= 1) {
    gqFunnels = await fetchContentfulGql({
      query: GET_FUNNELS,
      variables: {
        locale: contentfulLocale,
        preview: !!preview,
        ids: funnelIds,
      },
      preview,
    });

    // eslint-disable-next-line
    // @ts-ignore
    entry.content.items.map((item, i) => {
      // eslint-disable-next-line
      // @ts-ignore
      if (gqFunnels && gqFunnels.data && item.__typename === 'BlockFunnel') {
        // eslint-disable-next-line
        // @ts-ignore
        gqFunnels.data.blockFunnelCollection.items.map((funnel) => {
          if (funnel.sys.id === item.sys.id) {
            entry.content.items[i] = funnel;
          }
        });
      }
    });
  }

  const tagsSlugs = [];
  for (const tag of entry.linkedTags.items) {
    tagsSlugs.push(tag.slug);
  }

  const recentStoriesItems = [];
  const recentStories = await fetchContentfulGql({
    query: GET_BLOG_STORIES,
    variables: {
      locale: contentfulLocale,
      preview: !!preview,
      tags: tagsSlugs,
    },
    preview,
  });

  for (const item of recentStories.data.collection.items) {
    recentStoriesItems.push(...item.linkedFrom.articles.items);
  }

  const sortedRecentStories = recentStoriesItems
    .filter(
      (item): item is BlogItem =>
        item && !item.disableLocale && item.slug !== article,
    )
    .sort((a, b) => {
      const blogDateA =
        a.publishDate || a.sys.firstPublishedAt || a.sys.publishedAt;
      const blogDateB =
        b.publishDate || b.sys.firstPublishedAt || b.sys.publishedAt;

      return Date.parse(blogDateB) - Date.parse(blogDateA);
    });

  return {
    props: {
      entry,
      locale,
      preview,
      alternateSlugs: await getAlternatePages(entry?.sys?.id || '', preview),
      footer: getMainFooter(locale),
      authorBaseSlug: getAuthorsBaseSlug(locale),
      brand,
      recentStoriesItems: sortedRecentStories.slice(0, 3),
    },
    revalidate: config.defaults.secondsForPageRevalidation,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (skipServerSideBuild()) {
    return {
      paths: [],
      fallback: STATIC_PATH_FALLBACK,
    };
  }

  const paths: { params: { locale: string; article: string } }[] = [];

  const getEntries = config.locales.map(async (locale: ConfigLocale) => {
    const entries = await contentfulClientPreview.getEntries<IBlogPageFields>({
      content_type: 'blogPage',
      locale: locale.contentful,
      limit: 1000,
      select: 'fields.slug,fields.disableLocale',
    });

    for (const item of entries.items) {
      const { fields } = item;
      if (!fields) {
        continue;
      }

      if (!fields.disableLocale && fields.slug) {
        paths.push({
          params: {
            locale: locale.main,
            article: fields.slug,
          },
        });
      }
    }
  });

  await Promise.all(getEntries);

  return {
    paths,
    fallback: 'blocking',
  };
};

export default Article;
